import type {
  CRMUser,
  PeopleTravelDestination,
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
  PeopleTravelTripPurpose,
} from '../types';
import type { TravelCanvasSelection } from './peopleTravelCanvasGraph';
import { geocodeAddress } from './geocoding';
import { peopleTravelMemberLabel } from './peopleTravelTeamMembers';
import { auth, db } from './firebase';
import {
  TRAVEL_AGENT_ENDPOINT,
  TRAVEL_PLAN_POLL_TIMEOUT_MS,
  TRAVEL_PLAN_REQUESTS,
} from './travelAgentGuide';
import type { TravelPlanAttachment } from './travelPlannerInput';
import { tripDraftAdmitsMissingImage } from './tripPlanDraftGuard';

const VALID_PURPOSES: PeopleTravelTripPurpose[] = [
  'business',
  'conference',
  'client_visit',
  'personal',
  'other',
];

export interface TripPlanDraft {
  title?: string;
  purpose?: PeopleTravelTripPurpose;
  startDate?: string;
  endDate?: string;
  destinations?: PeopleTravelDestination[];
  travelerUserIds?: string[];
  budgetCap?: number;
  summary?: string;
  openQuestions?: string[];
  locations?: PeopleTravelLocation[];
  events?: PeopleTravelEvent[];
  legs?: PeopleTravelLeg[];
  proposedDays?: Array<{
    dayLabel?: string;
    events?: Array<{ title?: string; kind?: string; who?: string[]; locationLabel?: string }>;
  }>;
}

export type TravelCanvasSnapshot = {
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
};

export function coerceDraftList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw == null) return [];
  if (typeof raw === 'object') return [raw];
  if (typeof raw !== 'string') return [];
  const s = raw.trim();
  if (!s) return [];
  const tryParse = (text: string): unknown[] | null => {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object' && (parsed as { label?: string }).label) {
        return [parsed];
      }
    } catch {
      return null;
    }
    return null;
  };
  const parsed = tryParse(s) || tryParse(s.replace(/(-?\d+\.?\d*)[A-Za-z_]+\b/g, '$1'));
  if (parsed) return parsed;
  const labeled: Array<{ label: string }> = [];
  const labelRe = /"(?:label|name)"\s*:\s*"([^"]+)"/g;
  let labelMatch: RegExpExecArray | null = labelRe.exec(s);
  while (labelMatch) {
    labeled.push({ label: labelMatch[1] });
    labelMatch = labelRe.exec(s);
  }
  if (labeled.length) return labeled;
  if (/^[\[{]/.test(s)) return [];
  return [{ label: s }];
}

function normalizePurpose(raw: unknown): PeopleTravelTripPurpose | undefined {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return VALID_PURPOSES.includes(s as PeopleTravelTripPurpose)
    ? (s as PeopleTravelTripPurpose)
    : undefined;
}

function normalizeDate(raw: unknown): string | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim();
  if (!s) return undefined;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  return undefined;
}

function matchTravelerUids(names: unknown, teamMembers: CRMUser[]): string[] {
  if (!Array.isArray(names)) return [];
  const uids: string[] = [];
  for (const nameRaw of names) {
    const name = String(nameRaw).trim().toLowerCase();
    if (!name) continue;
    const hit =
      teamMembers.find(u => peopleTravelMemberLabel(u).toLowerCase() === name) ||
      teamMembers.find(u => peopleTravelMemberLabel(u).toLowerCase().includes(name)) ||
      teamMembers.find(u => name.includes(peopleTravelMemberLabel(u).toLowerCase()));
    if (hit && !uids.includes(hit.uid)) uids.push(hit.uid);
  }
  return uids;
}

/** Normalize draft JSON from the Mac-mini travel agent into CRM form fields. */
export function normalizeAgentTripPlanDraft(
  raw: Record<string, unknown>,
  teamMembers: CRMUser[],
): TripPlanDraft {
  const destinations: PeopleTravelDestination[] = [];
  const destItems = coerceDraftList(raw.destinations);
  for (const item of destItems) {
    if (typeof item === 'string' && item.trim()) {
      destinations.push({ label: item.trim() });
    } else if (item && typeof item === 'object') {
      const row = item as { label?: string; name?: string; lat?: number; lng?: number };
      const label = String(row.label || row.name || '').trim();
      if (!label) continue;
      destinations.push({
        label,
        lat: typeof row.lat === 'number' ? row.lat : undefined,
        lng: typeof row.lng === 'number' ? row.lng : undefined,
      });
    }
  }

  const travelerUserIds = Array.isArray(raw.travelerUserIds)
    ? raw.travelerUserIds.map(uid => String(uid).trim()).filter(Boolean)
    : matchTravelerUids(raw.travelerNames, teamMembers);

  const budgetRaw = raw.budgetCap;
  const budgetCap =
    typeof budgetRaw === 'number' && Number.isFinite(budgetRaw) && budgetRaw > 0
      ? budgetRaw
      : undefined;

  return {
    title: typeof raw.title === 'string' ? raw.title.trim() : undefined,
    purpose: normalizePurpose(raw.purpose),
    startDate: normalizeDate(raw.startDate),
    endDate: normalizeDate(raw.endDate),
    destinations: destinations.length ? destinations : undefined,
    travelerUserIds: travelerUserIds.length ? travelerUserIds : undefined,
    budgetCap,
    summary: typeof raw.summary === 'string' ? raw.summary.trim() : undefined,
    openQuestions: Array.isArray(raw.openQuestions)
      ? raw.openQuestions.map(q => String(q).trim()).filter(Boolean)
      : undefined,
    locations: coerceDraftList(raw.locations) as PeopleTravelLocation[],
    events: coerceDraftList(raw.events) as PeopleTravelEvent[],
    legs: coerceDraftList(raw.legs) as PeopleTravelLeg[],
    proposedDays: coerceDraftList(raw.proposedDays) as TripPlanDraft['proposedDays'],
  };
}

export async function enrichTripPlanDestinations(
  draft: TripPlanDraft,
): Promise<TripPlanDraft> {
  if (!draft.destinations?.length) return draft;
  const enriched: PeopleTravelDestination[] = [];
  for (const dest of draft.destinations) {
    if (dest.lat != null && dest.lng != null) {
      enriched.push(dest);
      continue;
    }
    const coords = await geocodeAddress(dest.label);
    enriched.push(
      coords ? { ...dest, lat: coords.lat, lng: coords.lng } : dest,
    );
  }
  return { ...draft, destinations: enriched };
}

async function getStaffIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error('Sign in required');
  return user.getIdToken();
}

export async function requestTripPlanFromPrompt(
  prompt: string,
  ownerUserId: string,
  teamMembers: CRMUser[],
  attachments: TravelPlanAttachment[] = [],
  tripId?: string,
  extras?: {
    selection?: TravelCanvasSelection | null;
    canvasSnapshot?: TravelCanvasSnapshot;
  },
): Promise<string> {
  const token = await getStaffIdToken();
  const response = await fetch(TRAVEL_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      action: 'requestTripPlanFromPrompt',
      prompt: prompt.trim(),
      ownerUserId,
      source: 'crm_planner',
      attachments,
      tripId: tripId || undefined,
      canvasSelection: extras?.selection
        ? JSON.parse(JSON.stringify(extras.selection))
        : undefined,
      canvasSnapshot: extras?.canvasSnapshot
        ? JSON.parse(JSON.stringify(extras.canvasSnapshot))
        : undefined,
      teamRoster: teamMembers.map(u => ({
        uid: u.uid,
        label: peopleTravelMemberLabel(u),
      })),
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Travel agent request failed (HTTP ${response.status})`);
  }
  const requestId = typeof data.requestId === 'string' ? data.requestId : '';
  if (!requestId) throw new Error('Travel agent did not return a request id');
  return requestId;
}

const TRAVEL_PLAN_WAIT_ERROR =
  'The trip planner did not finish in time. The job was queued — wait a minute and send again if no reply shows up.';

function draftFromPlanRequest(
  data: Record<string, unknown> | undefined,
  teamMembers: CRMUser[],
): { draft: TripPlanDraft; tripId?: string } | { error: string } | null {
  if (!data) return null;
  if (data.status === 'complete' && data.draft && typeof data.draft === 'object') {
    const rawDraft = data.draft as Record<string, unknown>;
    if (tripDraftAdmitsMissingImage(rawDraft)) {
      return {
        error: 'Planner did not read the attached screenshot. Try again — the image should be included automatically.',
      };
    }
    return {
      draft: normalizeAgentTripPlanDraft(rawDraft, teamMembers),
      tripId: typeof data.tripId === 'string' ? data.tripId : undefined,
    };
  }
  if (data.status === 'failed') {
    return { error: (typeof data.error === 'string' && data.error) || 'Travel agent plan failed' };
  }
  return null;
}

export function waitForTripPlanRequest(
  requestId: string,
  teamMembers: CRMUser[],
): Promise<{ draft: TripPlanDraft; tripId?: string }> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = (draft: TripPlanDraft, tripId?: string) => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      clearTimeout(timer);
      resolve({ draft, tripId });
    };

    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      clearTimeout(timer);
      reject(err);
    };

    const applyData = (data: Record<string, unknown> | undefined): boolean => {
      const read = draftFromPlanRequest(data, teamMembers);
      if (!read) return false;
      if ('error' in read) {
        fail(new Error(read.error));
        return true;
      }
      finish(read.draft, read.tripId);
      return true;
    };

    const timer = setTimeout(() => {
      void import('firebase/firestore').then(async ({ doc, getDoc }) => {
        try {
          const snap = await getDoc(doc(db, TRAVEL_PLAN_REQUESTS, requestId));
          if (snap.exists() && applyData(snap.data() as Record<string, unknown>)) return;
        } catch {
          /* fall through to timeout */
        }
        fail(new Error(TRAVEL_PLAN_WAIT_ERROR));
      });
    }, TRAVEL_PLAN_POLL_TIMEOUT_MS);

    import('firebase/firestore').then(({ doc, onSnapshot }) => {
      unsubscribe = onSnapshot(doc(db, TRAVEL_PLAN_REQUESTS, requestId), snap => {
        if (!snap.exists()) return;
        applyData(snap.data() as Record<string, unknown>);
      });
    });
  });
}

/** Queue a planner chat turn and wait for the structured draft. */
export async function planTripFromPrompt(args: {
  prompt: string;
  ownerUserId: string;
  teamMembers: CRMUser[];
  attachments?: TravelPlanAttachment[];
  tripId?: string;
  selection?: TravelCanvasSelection | null;
  canvasSnapshot?: TravelCanvasSnapshot;
}): Promise<{ draft: TripPlanDraft; requestId: string; tripId?: string }> {
  const trimmed = args.prompt.trim();
  const attachments = args.attachments || [];
  if (!trimmed && attachments.length === 0) {
    throw new Error('Paste trip notes or attach a screenshot first.');
  }

  const requestId = await requestTripPlanFromPrompt(
    trimmed,
    args.ownerUserId,
    args.teamMembers,
    attachments,
    args.tripId,
    {
      selection: args.selection,
      canvasSnapshot: args.canvasSnapshot,
    },
  );
  const result = await waitForTripPlanRequest(requestId, args.teamMembers);
  const enriched = await enrichTripPlanDestinations(result.draft);
  return { draft: enriched, requestId, tripId: result.tripId };
}

export async function publishMyTripPlanDrafts(): Promise<{ tripIds: string[] }> {
  const token = await getStaffIdToken();
  const response = await fetch(TRAVEL_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action: 'publishMyTripPlanDrafts' }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Could not publish trip plans (HTTP ${response.status})`);
  }
  const tripIds = Array.isArray(data.tripIds) ? data.tripIds.map(String) : [];
  return { tripIds };
}
